import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  NgZone,
  OnChanges,
  Output,
  PLATFORM_ID,
  SimpleChanges,
  inject,
} from '@angular/core';
import {
  animate,
  group,
  query,
  sequence,
  stagger,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { RouterModule } from '@angular/router';

/**
 * Icon geometry kept as raw primitives so one generic <svg> template draws every
 * glyph — same approach as the Services mega menu.
 *   p — path `d`        c — [cx, cy, r]
 */
export interface MoreIcon {
  p?: string[];
  c?: [number, number, number][];
}

export interface MoreItem {
  title: string;
  desc: string;
  /** Router path. Omitted while the destination page does not exist yet. */
  link?: string;
  icon: MoreIcon;
}

const ICONS = {
  leadership: {
    c: [[9.2, 8, 3.2]],
    p: [
      'M3.4 20a5.8 5.8 0 0 1 11.6 0',
      'M16.4 5.4a3.2 3.2 0 0 1 0 5.2',
      'M17.6 13.8A5.4 5.4 0 0 1 20.8 19',
    ],
  },
  internship: {
    c: [[10.1, 12.2, 1.8]],
    p: [
      'M5.6 6.6h12.8A1.6 1.6 0 0 1 20 8.2V18a1.6 1.6 0 0 1-1.6 1.6H5.6A1.6 1.6 0 0 1 4 18V8.2a1.6 1.6 0 0 1 1.6-1.6Z',
      'M9.1 6.6V5.4A1.4 1.4 0 0 1 10.5 4h3A1.4 1.4 0 0 1 14.9 5.4v1.2',
      'M7.1 16.6a3.4 3.4 0 0 1 6 0',
      'M15.4 11.2h2.2M15.4 14h2.2',
    ],
  },
  training: {
    p: [
      'm12 4.2 8.8 4.1L12 12.4 3.2 8.3Z',
      'M6.7 10.3v4.3c0 1.55 2.37 2.8 5.3 2.8s5.3-1.25 5.3-2.8v-4.3',
      'M20.1 9.4v5.2',
    ],
  },
  payment: {
    p: [
      'M3.4 7.6A1.8 1.8 0 0 1 5.2 5.8h13.6a1.8 1.8 0 0 1 1.8 1.8v8.8a1.8 1.8 0 0 1-1.8 1.8H5.2a1.8 1.8 0 0 1-1.8-1.8Z',
      'M3.4 10.2h17.2',
      'M6.8 14.4h3.4',
    ],
  },
  gallery: {
    c: [[9.1, 9.5, 1.5]],
    p: [
      'M4.2 6.2A1.8 1.8 0 0 1 6 4.4h12a1.8 1.8 0 0 1 1.8 1.8v11.6a1.8 1.8 0 0 1-1.8 1.8H6a1.8 1.8 0 0 1-1.8-1.8Z',
      'm4.6 16.4 4.3-4a1.7 1.7 0 0 1 2.3 0l3.1 2.9',
      'm13.4 13.6 1.7-1.5a1.7 1.7 0 0 1 2.3 0l2 1.8',
    ],
  },
} satisfies Record<string, MoreIcon>;

/**
 * "More" mega menu.
 *
 * A fixed overlay that anchors itself under the floating navbar pill and points
 * at the trigger. The header owns the open state (hover intent on desktop, tap
 * on mobile) exactly like `app-services-mega-menu`; everything visual and every
 * transition lives here.
 */
@Component({
  selector: 'app-more-mega-menu',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './more-mega-menu.component.html',
  styleUrl: './more-mega-menu.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('scrim', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('260ms ease-out', style({ opacity: 1 })),
      ]),
      transition(':leave', [animate('220ms ease-in', style({ opacity: 0 }))]),
    ]),
    /**
     * Fade + slide down in 280ms — the spec's 250–300ms window. The card
     * cascade is queried from this same transition rather than living in a
     * nested trigger: a child trigger would be blocked while its parent
     * animates in.
     */
    trigger('panel', [
      transition(':enter', [
        group([
          sequence([
            style({ opacity: 0, transform: 'translateY(-16px) scale(0.985)' }),
            animate(
              '280ms cubic-bezier(0.22, 1, 0.36, 1)',
              style({ opacity: 1, transform: 'none' })
            ),
          ]),
          query(
            '.mmm-item',
            [
              style({ opacity: 0, transform: 'translateY(12px)' }),
              stagger(26, [
                animate(
                  '300ms 60ms cubic-bezier(0.22, 1, 0.36, 1)',
                  style({ opacity: 1, transform: 'none' })
                ),
              ]),
            ],
            { optional: true }
          ),
          query(
            '.mmm-cta',
            [
              style({ opacity: 0, transform: 'translateY(14px)' }),
              animate(
                '320ms 160ms cubic-bezier(0.22, 1, 0.36, 1)',
                style({ opacity: 1, transform: 'none' })
              ),
            ],
            { optional: true }
          ),
        ]),
      ]),
      transition(':leave', [
        animate(
          '240ms cubic-bezier(0.4, 0, 1, 1)',
          style({ opacity: 0, transform: 'translateY(-12px)' })
        ),
      ]),
    ]),
  ],
})
export class MoreMegaMenuComponent implements OnChanges {
  /** Driven by the header — hover intent or a tap on the More trigger. */
  @Input() open = false;

  /** Pointer entered/left the panel; the header uses these to hold it open. */
  @Output() hoverIn = new EventEmitter<void>();
  @Output() hoverOut = new EventEmitter<void>();
  /** Scrim click, close button, or a link navigation. */
  @Output() closed = new EventEmitter<void>();

  @HostBinding('class.is-open') get isOpen(): boolean {
    return this.open;
  }
  @HostBinding('attr.aria-hidden') get ariaHidden(): string | null {
    return this.open ? null : 'true';
  }

  /**
   * Exactly the five entries the original More flyout carried, in their
   * original order and under their original labels. Nothing is added here —
   * every item maps to a page that already exists.
   */
  readonly items: MoreItem[] = [
    {
      title: 'Payment Partners',
      desc: 'Secure, trusted ways to pay for your project.',
      link: '/payment',
      icon: ICONS.payment,
    },
    {
      title: 'Gallery',
      desc: 'Moments, milestones and life at ResilienceSoft.',
      link: '/gallery',
      icon: ICONS.gallery,
    },
    {
      title: 'Meet The Team',
      desc: 'The people building your next digital product.',
      link: '/meettheteam',
      icon: ICONS.leadership,
    },
    {
      title: 'Internship',
      desc: 'Kickstart your career with our internship programs.',
      link: '/internship',
      icon: ICONS.internship,
    },
    {
      title: 'Training',
      desc: 'Upskill with our trainings and learning resources.',
      link: '/training',
      icon: ICONS.training,
    },
  ];

  private host = inject<ElementRef<HTMLElement>>(ElementRef);
  private zone = inject(NgZone);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['open'] || !this.open || !this.isBrowser) return;
    // The navbar pill shrinks on scroll and the trigger shifts with the layout,
    // so both are measured every time the menu opens rather than hard-coded.
    this.zone.runOutsideAngular(() => requestAnimationFrame(() => this.anchor()));
  }

  requestClose(): void {
    this.closed.emit();
  }

  trackByTitle(_i: number, item: MoreItem): string {
    return item.title;
  }

  /** Writes the panel offset and the pointer's x position onto the host. */
  private anchor(): void {
    const root = this.host.nativeElement;
    const nav = document.querySelector<HTMLElement>('nav.navbar');
    const trigger = document.querySelector<HTMLElement>('.more-trigger');

    const top = nav ? nav.getBoundingClientRect().bottom + 12 : 104;
    root.style.setProperty('--mmm-top', `${Math.round(top)}px`);

    if (trigger) {
      const r = trigger.getBoundingClientRect();
      root.style.setProperty('--mmm-arrow-x', `${Math.round(r.left + r.width / 2)}px`);
    }
  }
}
